import { useEffect, useRef, useState } from 'react';
import { useConfig } from '../context/ConfigContext';
import { formatMoney } from '../utils/currencyUtil';
import { useAnimatedNumber } from '../hooks/useAnimatedNumber';
import { useReducedMotion } from '../hooks/useReducedMotion';
import {
  LANDING_BRAND_NAME,
  LANDING_BRAND_SUFFIX,
  LANDING_DEMO_CATALOGUE,
  LANDING_DEMO_PAY_MODES,
  LANDING_DEMO_RESET_MS,
} from '../constants/posLandingConst';

/**
 * The hero's working till: tap an item, it lands on the order and its stock
 * drops; take payment and the order clears.
 *
 * Entirely local — it writes nothing and the stock is illustrative. Its job
 * is to let a visitor feel the counter before they sign up.
 *
 * @returns {JSX.Element}
 */
function PosLandingTillDemo() {
  const { config } = useConfig();
  const reduced = useReducedMotion();
  const [catalogue, setCatalogue] = useState(() => LANDING_DEMO_CATALOGUE.map((item) => ({ ...item })));
  const [order, setOrder] = useState([]);
  const [payMode, setPayMode] = useState(LANDING_DEMO_PAY_MODES[0].key);
  const [paid, setPaid] = useState(false);
  const resetTimer = useRef(null);

  const total = order.reduce((sum, line) => sum + line.price * line.qty, 0);
  const shownTotal = useAnimatedNumber(total);

  // Cleared on unmount so a reset scheduled just before leaving the page
  // never sets state on an unmounted component.
  useEffect(() => {
    const timer = resetTimer;
    return () => clearTimeout(timer.current);
  }, []);

  /**
   * Rings one of an item up: one off its stock, one onto the order.
   *
   * @param {number} index - into the catalogue
   * @param {HTMLButtonElement} tile - flashed to acknowledge the tap
   * @returns {void}
   */
  const handleAdd = (index, tile) => {
    const item = catalogue[index];
    if (!item || item.stock <= 0) return;
    setCatalogue((current) => current.map((entry, i) => (i === index ? { ...entry, stock: entry.stock - 1 } : entry)));
    setOrder((current) => {
      const found = current.find((line) => line.name === item.name);
      if (found) return current.map((line) => (line.name === item.name ? { ...line, qty: line.qty + 1 } : line));
      return [...current, { name: item.name, price: item.price, qty: 1 }];
    });
    setPaid(false);
    if (!reduced && tile) {
      // Toggled on the element itself rather than through state: removing
      // the class and forcing a reflow is what restarts the animation on a
      // repeat tap, and React never rewrites this class because the tile's
      // own className prop does not change.
      tile.classList.remove('flash');
      // eslint-disable-next-line no-unused-expressions
      tile.offsetWidth;
      tile.classList.add('flash');
    }
  };

  /** Takes payment, then clears the order a moment later. @returns {void} */
  const handlePay = () => {
    if (!order.length) return;
    setPaid(true);
    clearTimeout(resetTimer.current);
    resetTimer.current = setTimeout(() => {
      setOrder([]);
      setPaid(false);
    }, LANDING_DEMO_RESET_MS);
  };

  return (
    <div className="till" id="demo">
      <div className="till-bar">
        <i /><i /><i />
        <small>Your Business Name • {LANDING_BRAND_NAME} {LANDING_BRAND_SUFFIX}</small>
      </div>
      <div className="till-body">
        <div>
          <p className="till-hint">Tap an item to ring it up</p>
          <div className="tiles">
            {catalogue.map((item, index) => (
              <button
                key={item.name}
                type="button"
                className="tile"
                disabled={item.stock <= 0}
                aria-label={`Add ${item.name}, ${formatMoney(item.price, config)}`}
                onClick={(event) => handleAdd(index, event.currentTarget)}
                onAnimationEnd={(event) => event.currentTarget.classList.remove('flash')}
              >
                <b>{item.name}</b>
                <span className="tile-row">
                  <span className="p money">{formatMoney(item.price, config)}</span>
                  <span className="s">{item.stock > 0 ? `${item.stock} in stock` : 'Out of stock'}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
        <div className="order">
          <div className="lines">
            {order.length === 0 ? (
              <p className="empty">Nothing on the order yet.</p>
            ) : order.map((line) => (
              <div className="line" key={line.name}>
                <span>{line.qty}×</span>
                <span>{line.name}</span>
                <span className="money">{formatMoney(line.price * line.qty, config)}</span>
              </div>
            ))}
          </div>
          <div className="total"><small>Total</small><b className="money">{formatMoney(shownTotal, config)}</b></div>
          <p className="paylabel">Mode of payment</p>
          <div className="pays" role="group" aria-label="Mode of payment">
            {LANDING_DEMO_PAY_MODES.map((mode) => (
              <button
                key={mode.key}
                type="button"
                className={`paybtn${payMode === mode.key ? ' on' : ''}`}
                onClick={() => setPayMode(mode.key)}
              >
                <span>{mode.symbol}</span>{mode.key}
              </button>
            ))}
          </div>
          <button type="button" className={`pay${paid ? ' done' : ''}`} disabled={total === 0} onClick={handlePay}>
            {paid ? `Paid with ${payMode} — receipt saved` : 'Take payment'}
          </button>
        </div>
      </div>
      <p className="till-note">Instant updates, zero delays. A perfectly simplified system where your stock counts down the exact second you sell.</p>
    </div>
  );
}

export default PosLandingTillDemo;
